'use client';

import { useMemo, useState } from 'react';
import { HubNewsItem, HubNewsTagCategoryItem, HubNewsTagSelection } from '@/types/hubs';
import SectionTitle from '@/components/SectionTitle';
import { getPanelVisibleTags } from '@/(routers)/hubs/cases/components/hubNewsTags';
import HubNewsSearchPillUnit from '@/(routers)/hubs/cases/components/unit/HubNewsSearchPillUnit';

export interface HubNewsSearchDetailProps {
    categories: HubNewsTagCategoryItem[];
    selection: HubNewsTagSelection;
    hasTaggedNews: boolean;
    calcResult: (selection: HubNewsTagSelection) => { newsList: HubNewsItem[]; tagCounts: Record<string, number> };
    onApply: (selection: HubNewsTagSelection) => void;
    onClose: () => void;
}

export default function HubNewsSearchDetail({
    categories,
    selection,
    hasTaggedNews,
    calcResult,
    onApply,
    onClose,
}: HubNewsSearchDetailProps) {
    const [draftSelection, setDraftSelection] = useState<HubNewsTagSelection>(selection);

    const { newsList, tagCounts } = useMemo(() => calcResult(draftSelection), [calcResult, draftSelection]);
    const resultCount = newsList.length;

    const handleToggleTag = (categoryCd: string, tagCd: string) => {
        setDraftSelection((prev) => {
            const current = prev[categoryCd] ?? [];
            const next = current.includes(tagCd) ? current.filter((cd) => cd !== tagCd) : [...current, tagCd];
            return { ...prev, [categoryCd]: next };
        });
    };

    return (
        <div className="flex flex-col w-full h-full">
            <div className="flex flex-col flex-grow w-full overflow-y-auto px-5 md:px-8 pb-4">
                <div className="font-montserrat font-black text-lg text-color-base tracking-widest pb-4">
                    条件を選択
                </div>
                <div className="flex flex-col w-full gap-6">
                    {categories.map((category) => {
                        const selectedTagCds = draftSelection[category.cd] ?? [];
                        const visibleTags = getPanelVisibleTags(category, draftSelection, hasTaggedNews);
                        if (visibleTags.length === 0) return null;

                        return (
                            <div key={category.cd} className="flex flex-col w-full gap-2">
                                <SectionTitle title={category.name} />
                                <div className="flex flex-wrap gap-2">
                                    {visibleTags.map((tag) => (
                                        <HubNewsSearchPillUnit
                                            key={tag.cd}
                                            label={tag.name}
                                            count={tagCounts[tag.cd] ?? 0}
                                            isSelected={selectedTagCds.includes(tag.cd)}
                                            onClick={() => handleToggleTag(category.cd, tag.cd)}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="flex flex-col w-full gap-2 px-5 md:px-8 py-4 border-t border-color-co-gray-light bg-color-white-base">
                {resultCount === 0 && (
                    <div className="font-notojp font-medium text-xs text-color-error">
                        この条件に当てはまる事例がありません。条件を減らしてください。
                    </div>
                )}
                <div className="flex items-center w-full">
                    <button
                        type="button"
                        disabled={resultCount === 0}
                        className="flex flex-grow justify-center items-center gap-2 h-11 px-4 md:px-6 rounded-full bg-color-base text-color-white-base font-notojp font-bold text-xs md:text-sm hover:brightness-125 transition-all duration-150 disabled:bg-color-button-disabled disabled:cursor-not-allowed disabled:hover:brightness-100"
                        onClick={() => {
                            onApply(draftSelection);
                            onClose();
                        }}
                    >
                        この条件で絞り込む
                        <span className="font-cantarell font-bold text-xs text-color-white-base/70 tabular-nums">
                            {resultCount}件
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
}
